import React, { useState } from "react";
import { Box, Button, CircularProgress } from "@mui/material";
import api from "../../../services/api";
import { useSnackbarAlert } from "../../../hooks/useSnackbarAlert";

const SubmitActions = ({
  step,
  isLastStep,
  nextStep,
  prevStep,
  goToStep,
  validateStep,
  validateAll,
  formData,
  resetFormData,
}) => {
  const snackbar = useSnackbarAlert();
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const buildOrderPayload = (formData) => {
    const { customer, fulfillment, items, payment, meta } = formData;

    const normalizedItems = items.map((item) => ({
      productId: item.productId || null,
      name: item.name,
      displayName: item.displayName,
      category: item.category,
      subcategory: item.subcategory || null,
      flavor: item.flavor || null,
      size: item.size || null,
      quantity: item.quantity,
      unitPrice: item.unitPrice,
      totalPrice: item.totalPrice,
      customizations: item.customizations || [],
      note: item.note,
      discount: { amount: 0, type: null, name: null },
      image: item.image || null,
      referenceImages: (item.referenceImages || []).map(
        ({ url, purpose, customPurpose }) => ({
          url,
          purpose,
          customPurpose: purpose === "other" ? customPurpose : undefined,
        })
      ),
    }));

    return {
      customer,
      fulfillment,
      items: normalizedItems,
      payment,
      meta: {
        ...meta,
        status: "in-progress",
        source: "admin-manual",
        createdAt: new Date(),
      },
      internal: formData.internal || {},
    };
  };

  const handleSubmit = async () => {
    const isValid = validateAll();
    if (!isValid) return;

    setIsSubmitting(true);
    const payload = buildOrderPayload(formData);

    try {
      const res = await api.post("/admin/orders", payload);
      if (res.status === 201 || res.data?.orderId) {
        snackbar.success("Order submitted successfully");

        // Queue next snackbar after 1s
        const draftId = formData.meta?.draftId;
        if (draftId) {
          try {
            const deleteRes = await api.delete(
              `/admin/draft-orders/${draftId}`
            );

            setTimeout(() => {
              if (deleteRes.status === 200) {
                snackbar.success("Draft deleted after submission.");
              } else {
                snackbar.error("Draft deletion was not successful");
              }
            }, 1000); // delay the second snackbar by 1 second
          } catch (deleteErr) {
            console.error(
              "❌ Failed to delete draft after submission",
              deleteErr
            );
          }
        }

        // Reset form after all snackbars
        setTimeout(() => {
          resetFormData();
          goToStep(0);
        }, 2500); // delay reset to let snackbars finish showing
      } else {
        snackbar.error("Unexpected response from server.");
      }
    } catch (err) {
      console.error("Failed to submit order:", err);
      snackbar.error("Failed to submit order. See console for details.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSaveDraft = async () => {
    setIsSavingDraft(true);
    const payload = {
      formData,
      savedAt: new Date(),
    };

    try {
      let res;

      if (formData.meta?.draftId) {
        // Update existing draft
        res = await api.put(
          `/admin/draft-orders/${formData.meta.draftId}`,
          payload
        );
      } else {
        // Create new draft
        res = await api.post("/admin/draft-orders", payload);
      }

      if (res.status === 200 || res.status === 201) {
        snackbar.success("Draft saved successfully");
        setTimeout(() => {
          resetFormData();
          goToStep(0);
        }, 2000);
      } else {
        snackbar.error("Unexpected response while saving draft.");
      }
    } catch (err) {
      console.error("Failed to save draft:", err);
      snackbar.error("Failed to save draft. See console for details.");
    } finally {
      setIsSavingDraft(false);
    }
  };

  const handleNextWithValidation = () => {
    const isValid = validateStep(step);
    if (!isValid) return;
    nextStep();
  };

  return (
    <Box display="flex" justifyContent="space-between" mt={3}>
      <Button variant="outlined" disabled={step === 0} onClick={prevStep}>
        Back
      </Button>

      <Box display="flex" gap={1}>
        <Button
          variant="outlined"
          onClick={handleSaveDraft}
          disabled={isSavingDraft || isSubmitting}>
          {isSavingDraft ? <CircularProgress size={20} /> : "Save as Draft"}
        </Button>

        {!isLastStep && (
          <Button variant="outlined" onClick={nextStep}>
            Skip
          </Button>
        )}

        {isLastStep ? (
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={isSubmitting || isSavingDraft}>
            {isSubmitting ? <CircularProgress size={20} /> : "Submit Order"}
          </Button>
        ) : (
          <Button variant="contained" onClick={handleNextWithValidation}>
            Continue
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default SubmitActions;
